/**
 * This file contains valid types and string literal arrays for aria roles
 * https://www.w3.org/WAI/PF/aria/roles#role_definitions_header
 */

export type Role = (typeof roles)[number];

export type CompositeWidgetRole = (typeof compositeWidget)[number];
export type ContentRole = (typeof content)[number];
export type LandmarkRole = (typeof landmark)[number];
export type LiveRegionRole = (typeof liveRegion)[number];
export type WidgetRole = (typeof widget)[number];

// Roles that act as standalone user interface components or as part of a larger composition
const widget = [
  'alertdialog',
  'button',
  'checkbox',
  'dialog',
  'gridcell',
  'link',
  'menuitem',
  'menuitemcheckbox',
  'menuitemradio',
  'option',
  'progressbar',
  'radio',
  'scrollbar',
  'slider',
  'spinbutton',
  'switch',
  'tab',
  'tabpanel',
  'textbox',
  'tooltip',
  'treeitem'
] as const;

const button = [
  'checkbox',
  'link',
  'menuitem',
  'menuitemcheckbox',
  'menuitemradio',
  'radio',
  'switch',
  'tab'
] as const;

// Roles that act as composite components. These roles typically act as containers that manage other, contained components.
const compositeWidget = [
  'combobox',
  'grid',
  'listbox',
  'menu',
  'menubar',
  'radiogroup',
  'tablist',
  'tree',
  'treegrid'
] as const;

// Roles that describe structures that organize content. Document structures are not usually interactive.
const content = [
  'article',
  'columnheader',
  'definition',
  'directory',
  'document',
  'group',
  'heading',
  'img',
  'list',
  'listitem',
  'math',
  'note',
  'presentation',
  'region',
  'row',
  'rowheader',
  'separator',
  'toolbar'
] as const;

const liveRegion = ['alert', 'log', 'marquee', 'status', 'timer'] as const;

// Roles that act as navigational landmarks - should appear only once per page
const landmark = [
  'application',
  'banner',
  'complementary',
  'contentinfo',
  'form',
  'main',
  'navigation',
  'search'
] as const;

const roles = Object.freeze({
  ...button,
  ...compositeWidget,
  ...content,
  ...landmark,
  ...liveRegion,
  ...widget
});

export { button, compositeWidget, content, landmark, liveRegion, widget };
